import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Search,
  Link2,
  Zap,
  Cog,
  Database,
  BarChart3,
  Lightbulb,
  Calculator,
  FileText,
  ClipboardList,
  Rocket,
  AlignLeft,
  ArrowDown,
  Sparkles,
  Users,
  Cloud,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const TESTIMONIALS = [
  {
    quote:
      "Simpel's Automation Audit gave us deep visibility into our operational bottlenecks and delivered a clear, prioritized roadmap that saved us months of guesswork.",
    name: "Sarah Jenkins",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of IT & Infrastructure • Apex Dynamics",
  },
  {
    quote:
      "Simpel's solution helped us streamline our operations and uncover hidden automation opportunities. The team's expertise and actionable recommendations have been invaluable.",
    name: "Tejas Karwa",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Project Manager • Aptia UK",
  },
  {
    quote:
      "The audit delivered immediate clarity across systems and teams, cutting process latency and driving measurable ROI right from the first phase.",
    name: "David Chen",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of Digital Transformation • Enterprise Systems",
  },
];

const UNCOVER_PILLARS = [
  {
    icon: Search,
    iconColor: "#0284C7",
    iconBg: "rgba(224, 242, 254, 1)",
    titleLine1: "Gain Process",
    titleLine2: "Visibility",
    desc: "Understand how your work really happens across teams and systems.",
  },
  {
    icon: Link2,
    iconColor: "#A855F7",
    iconBg: "rgba(243, 232, 255, 1)",
    titleLine1: "Identify Automation",
    titleLine2: "Gaps",
    desc: "Uncover inefficiencies, bottlenecks, and high impact automation areas.",
  },
  {
    icon: Zap,
    iconColor: "#10B981",
    iconBg: "rgba(220, 252, 231, 1)",
    titleLine1: "Prioritize What",
    titleLine2: "Matters",
    desc: "Focus on opportunities that deliver the highest business value and ROI.",
  },
];

const AUDIT_SERVICES = [
  {
    icon: Cog,
    iconColor: "#0284C7",
    iconBg: "rgba(224, 242, 254, 1)",
    title: "Process Analysis",
    desc: "Map and analyze your end-to-end business processes.",
  },
  {
    icon: Database,
    iconColor: "#EA580C",
    iconBg: "rgba(255, 237, 213, 1)",
    title: "Tool & System Review",
    desc: "Evaluate existing tools, systems, and integrations for automation readiness.",
  },
  {
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "rgba(220, 252, 231, 1)",
    title: "Efficiency Assessment",
    desc: "Identify manual tasks, redundancies, and performance gaps.",
  },
  {
    icon: Lightbulb,
    iconColor: "#9333EA",
    iconBg: "rgba(243, 232, 255, 1)",
    title: "Automation Opportunities",
    desc: "Uncover high-impact use cases for RPA, AI, and workflow automation.",
  },
  {
    icon: Calculator,
    iconColor: "#DB2777",
    iconBg: "rgba(252, 231, 243, 1)",
    title: "Cost-Benefit Analysis",
    desc: "Estimate potential savings and business impact.",
  },
  {
    icon: FileText,
    iconColor: "#CA8A04",
    iconBg: "rgba(254, 249, 195, 1)",
    title: "Roadmap & Recommendations",
    desc: "Provide a clear, prioritized roadmap for implementation.",
  },
];

const APPROACH_STEPS = [
  {
    step: "01",
    title: "Assess",
    desc: "Map processes and gather data",
    icon: ClipboardList,
    iconColor: "#0284C7",
    iconBg: "rgba(186, 230, 253, 1)",
    glowColor: "rgba(56, 189, 248, 0.45)",
  },
  {
    step: "02",
    title: "Analyze",
    desc: "Identify gaps and automation potential",
    icon: Search,
    iconColor: "#9333EA",
    iconBg: "rgba(243, 232, 255, 1)",
    glowColor: "rgba(217, 70, 239, 0.45)",
  },
  {
    step: "03",
    title: "Prioritize",
    desc: "Evaluate impact and feasibility",
    icon: BarChart3,
    iconColor: "#CA8A04",
    iconBg: "rgba(254, 240, 138, 1)",
    glowColor: "rgba(234, 179, 8, 0.45)",
  },
  {
    step: "04",
    title: "Recommend",
    desc: "Create a clear action plan",
    icon: Cog,
    iconColor: "#16A34A",
    iconBg: "rgba(187, 247, 208, 1)",
    glowColor: "rgba(34, 197, 94, 0.45)",
  },
  {
    step: "05",
    title: "Implement",
    desc: "Support execution and measure results",
    icon: Rocket,
    iconColor: "#E11D48",
    iconBg: "rgba(254, 205, 211, 1)",
    glowColor: "rgba(244, 63, 94, 0.45)",
  },
];

const AUDIT_IMPACT_POINTS = [
  {
    icon: Zap,
    text: "Reduce Manual Effort",
  },
  {
    icon: AlignLeft,
    text: "Improve Operational Efficiency",
  },
  {
    icon: ArrowDown,
    text: "Lower Costs",
  },
  {
    icon: Sparkles,
    text: "Enable Scalable Growth",
  },
];

const ECOSYSTEM_TAGS = [
  {
    name: "ERP",
    icon: () => (
      <span className="flex items-center gap-[1.5px] shrink-0">
        <span className="w-1 h-3 rounded-[1px] bg-[#22C55E]" />
        <span className="w-1 h-3 rounded-[1px] bg-[#3B82F6]" />
        <span className="w-1 h-3 rounded-[1px] bg-[#EF4444]" />
      </span>
    ),
  },
  {
    name: "CRM",
    icon: () => <Users className="w-3.5 h-3.5 text-[#38BDF8] shrink-0" />,
  },
  {
    name: "Databases",
    icon: () => <Database className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />,
  },
  {
    name: "APIs",
    icon: () => <Zap className="w-3.5 h-3.5 text-[#F59E0B] shrink-0" />,
  },
  {
    name: "Legacy Systems",
    icon: () => <Cloud className="w-3.5 h-3.5 text-[#00D2EE] shrink-0" />,
  },
];

const AutomationAuditPage = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleTalkToTeam = () => {
    openAgent("Automation Audit");
  };

  const handlePrevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div className="min-h-screen w-full bg-[#010710] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Top Navbar */}
      <StackNavbar />

      <main className="flex-1">
        {/* Hero Section matching Screenshot exactly */}
        <section className="relative w-full overflow-hidden bg-[#010710] pt-[76px] sm:pt-[80px] min-h-[500px] sm:min-h-[540px] lg:min-h-[570px] xl:min-h-[600px] flex items-center">
          {/* Background Illustration spanning the section, scaled cleanly and pinned to right */}
          <div className="absolute right-0 top-0 bottom-0 h-full w-full pointer-events-none select-none z-0 flex items-center justify-end overflow-hidden">
            <div className="relative h-full flex items-center justify-end w-auto">
              <img
                src="/images/services/automation-audit-hero.jpg"
                alt="Discover Automation Opportunities Across Your Business"
                className="h-[68%] sm:h-[75%] lg:h-[82%] xl:h-[86%] max-h-[460px] w-auto max-w-none object-contain object-right pointer-events-none select-none block"
              />
              {/* Soft feather gradient on the left edge of the graphic */}
              <div className="absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#010710] to-transparent pointer-events-none" />
            </div>
          </div>

          {/* Subtle fade overlay on mobile/tablet screens for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#010710] via-[#010710]/90 to-transparent lg:hidden pointer-events-none z-[1]" />

          {/* Section Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-12 lg:py-16 flex items-center">
            <div className="max-w-[480px] xl:max-w-[520px] flex flex-col items-start">
              {/* Breadcrumb matching mockup: WHAT WE DO > SERVICES > AUTOMATION AUDIT */}
              <div className="flex items-center gap-2 text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#7E93AC] select-none">
                <Link
                  to="/what-we-do"
                  className="hover:text-white transition-colors"
                >
                  WHAT WE DO
                </Link>
                <span>&gt;</span>
                <Link
                  to="/what-we-do#services"
                  className="hover:text-white transition-colors"
                >
                  SERVICES
                </Link>
                <span>&gt;</span>
                <span className="text-[#A2B6CE]">AUTOMATION AUDIT</span>
              </div>

              {/* Main Heading: 3 lines matching screenshot */}
              <h1 className="mt-5 text-[32px] sm:text-[38px] lg:text-[44px] xl:text-[48px] font-extrabold leading-[1.14] tracking-tight text-white">
                <span className="block">Discover Automation</span>
                <span className="block mt-1">Opportunities Across</span>
                <span className="block mt-1">Your Business.</span>
              </h1>

              {/* Subtitle / Description matching screenshot */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] text-[#9FB3C8] max-w-[460px] xl:max-w-[490px]">
                Identify inefficiencies, uncover automation opportunities, and create a clear roadmap to streamline operations and drive measurable value.
              </p>

              {/* CTA Button matching screenshot */}
              <div className="mt-7 sm:mt-8">
                <button
                  onClick={handleTalkToTeam}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-[14px] sm:text-[14.5px] font-bold text-[#0B132B] shadow-[0_0_28px_rgba(0,180,255,0.45)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,210,238,0.65)] active:scale-95 cursor-pointer"
                >
                  <span>Talk to our team</span>
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2.2}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Uncover Opportunities Section matching user screenshot */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(255, 255, 255, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-12 lg:gap-10">
            {/* Left Content Column */}
            <div className="w-full lg:w-[460px] xl:w-[490px] shrink-0 pt-1">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                UNCOVER OPPORTUNITIES
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[40px] xl:text-[44px] font-extrabold leading-[1.15] tracking-tight">
                <span className="block text-[#0F172A] sm:whitespace-nowrap">Turn Insights into</span>
                <span className="block text-[#00D2EE] sm:whitespace-nowrap mt-1">
                  Smarter Operations.
                </span>
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#556987] max-w-[450px]">
                Simpel’s Automation Audit assesses your existing processes, systems, and tools to identify automation opportunities that can reduce manual effort, improve efficiency, and deliver greater value for your business.
              </p>
            </div>

            {/* Right Cards Column */}
            <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-5 xl:gap-6">
              {UNCOVER_PILLARS.map((pillar) => {
                const IconComp = pillar.icon;
                return (
                  <div
                    key={pillar.titleLine1 + pillar.titleLine2}
                    className="flex flex-col justify-between p-7 sm:p-7 xl:p-8 rounded-[22px] bg-white border border-[#E2E8F0]/90 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_12px_36px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-300 select-none min-h-[250px] sm:min-h-[270px]"
                  >
                    <div>
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                        style={{ backgroundColor: pillar.iconBg }}
                      >
                        <IconComp
                          className="w-5 h-5"
                          style={{ color: pillar.iconColor }}
                          strokeWidth={2.4}
                        />
                      </div>

                      <h3 className="mt-6 text-[18px] sm:text-[19px] font-bold text-[#0F172A] leading-snug tracking-tight">
                        <span className="block">{pillar.titleLine1}</span>
                        <span className="block">{pillar.titleLine2}</span>
                      </h3>

                      <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                        {pillar.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comprehensive Automation Audit Services Section matching user screenshot */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(236, 245, 253, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px]">
            {/* Header Block: Left Title and Right Description with vertical divider */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-12 pb-12 sm:pb-14">
              <div className="max-w-[620px]">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                  SERVICE CAPABILITIES
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[40px] xl:text-[44px] font-extrabold leading-[1.15] tracking-tight text-[#0F172A]">
                  <span className="block sm:whitespace-nowrap">Comprehensive Automation</span>
                  <span className="block mt-1">Audit Services</span>
                </h2>
              </div>

              <div className="lg:border-l lg:border-[#CBD5E1] lg:pl-8 max-w-[460px]">
                <p className="text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#556987]">
                  Assess your existing processes, identify inefficiencies, and uncover automation opportunities with a practical roadmap aligned with your business goals.
                </p>
              </div>
            </div>

            {/* 6 Cards Grid (2 rows of 3) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {AUDIT_SERVICES.map((service) => {
                const IconComp = service.icon;
                return (
                  <div
                    key={service.title}
                    className="flex flex-col justify-start p-7 sm:p-8 rounded-[22px] bg-white border border-[#E2E8F0]/90 shadow-[0_4px_24px_rgba(0,0,0,0.03)] hover:shadow-[0_12px_36px_rgba(37,99,235,0.08)] hover:-translate-y-1 transition-all duration-300 select-none min-h-[220px]"
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: service.iconBg }}
                    >
                      <IconComp
                        className="w-5 h-5"
                        style={{ color: service.iconColor }}
                        strokeWidth={2.4}
                      />
                    </div>

                    <h3 className="mt-6 text-[18px] sm:text-[19px] font-bold text-[#0F172A] leading-snug tracking-tight">
                      {service.title}
                    </h3>

                    <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      {service.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* A Data-Driven Approach: From Analysis to Action Section */}
        <section
          className="relative w-full overflow-hidden py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(5, 10, 21, 1)" }}
        >
          {/* Background Illustration with subtle opacity matching Legacy Data Migration page */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-0">
            <img
              src="/images/services/legacy-migration-approach-bg.png"
              alt="A Data-Driven Approach Background"
              className="w-full h-full object-cover object-center pointer-events-none select-none block"
              style={{ opacity: 0.35 }}
            />
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header */}
            <div className="max-w-[760px]">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#CBD5E1]">
                A DATA-DRIVEN APPROACH
              </p>
              <h2 className="mt-3.5 text-[30px] sm:text-[36px] lg:text-[42px] font-extrabold leading-[1.15] tracking-tight text-white">
                From Analysis to Action.
              </h2>
              <p className="mt-4 text-[13.5px] sm:text-[14.5px] leading-[23px] sm:leading-[25px] text-[#9FB3C8]">
                We follow a structured approach to assess, identify, and prioritize automation opportunities for maximum impact.
              </p>
            </div>

            {/* 5-Step Process with Arrows */}
            <div className="mt-14 sm:mt-16 lg:mt-20 flex flex-col md:flex-row items-center md:items-start justify-between gap-8 md:gap-2 lg:gap-4">
              {APPROACH_STEPS.map((step, idx) => {
                const IconComp = step.icon;
                return (
                  <React.Fragment key={step.title}>
                    <div className="flex flex-col items-center text-center flex-1 max-w-[210px] group">
                      {/* Circular Badge with Soft Glow */}
                      <div
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-110"
                        style={{
                          backgroundColor: step.iconBg,
                          boxShadow: `0 0 32px ${step.glowColor}`,
                        }}
                      >
                        <IconComp
                          className="w-6 h-6 sm:w-7 sm:h-7"
                          style={{ color: step.iconColor }}
                          strokeWidth={2.3}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-5 text-[17px] sm:text-[18px] font-bold text-white tracking-tight">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[12.5px] sm:text-[13.5px] leading-[19px] sm:leading-[20px] text-[#94A3B8]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Cyan Arrow between steps */}
                    {idx < APPROACH_STEPS.length - 1 && (
                      <div className="hidden md:flex items-center justify-center mt-4 sm:mt-5 text-[#00D2EE] shrink-0">
                        <ArrowRight className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={2.4} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* Clear Insights. Real Business Impact. Section matching user mockup */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28 overflow-hidden"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-14 xl:gap-16">
              {/* Left Side: Dashboard Screen Graphic matching user mockup */}
              <div className="flex-1 w-full max-w-[640px] xl:max-w-[680px] flex items-center justify-center">
                <img
                  src="/images/services/automation-audit-dashboard.png"
                  alt="Automation Audit Dashboard - Clear Insights"
                  className="w-full h-auto object-contain block select-none pointer-events-none transition-transform duration-300 hover:scale-[1.01]"
                />
              </div>

              {/* Right Side: Content */}
              <div className="w-full lg:w-[480px] xl:w-[520px] shrink-0">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                  TURN INSIGHTS INTO RESULTS
                </p>
                <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[45px] font-extrabold text-[#0F172A] leading-[1.15] tracking-tight">
                  <span className="block">Clear Insights.</span>
                  <span className="block mt-1">
                    <span>Real </span>
                    <span className="text-[#00D2EE]">Business Impact.</span>
                  </span>
                </h2>
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#64748B]">
                  Our Automation Audit provides actionable insights that help you streamline operations, reduce costs, and build a smarter, more efficient business.
                </p>

                {/* 4 Feature Points with soft blue circular icon badges */}
                <div className="mt-8 sm:mt-9 flex flex-col gap-4 sm:gap-4.5">
                  {AUDIT_IMPACT_POINTS.map((point) => {
                    const IconComp = point.icon;
                    return (
                      <div key={point.text} className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#E0F2FE] flex items-center justify-center shrink-0">
                          <IconComp className="w-4 h-4 text-[#0284C7]" strokeWidth={2.4} />
                        </div>
                        <span className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] tracking-tight">
                          {point.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Connect Across Your Ecosystem Section matching user mockup */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28 overflow-hidden"
          style={{ backgroundColor: "rgba(6, 15, 30, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-14 xl:gap-16">
              {/* Left Side: Text and Badges */}
              <div className="w-full lg:w-[480px] xl:w-[520px] shrink-0">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#7E93AC] select-none">
                  CONNECT ACROSS YOUR ECOSYSTEM
                </p>
                <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[45px] font-extrabold text-white leading-[1.15] tracking-tight">
                  <span className="block">Migrate to the Platforms</span>
                  <span className="block mt-1">That Power Your Future.</span>
                </h2>
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#9FB3C8]">
                  We support migration across a wide range of platforms and systems, tailored to your business needs.
                </p>

                {/* 5 Ecosystem Badges / Pills matching screenshot */}
                <div className="mt-8 flex flex-wrap gap-2.5 sm:gap-3 max-w-[450px]">
                  {ECOSYSTEM_TAGS.map((tag) => {
                    const IconComp = tag.icon;
                    return (
                      <div
                        key={tag.name}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0E1A2C] border border-[#1E2E48] text-white text-[13px] sm:text-[13.5px] font-medium shadow-sm hover:border-[#00D2EE]/50 transition-colors select-none"
                      >
                        <IconComp />
                        <span>{tag.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Side: Graphic Illustration from user PNG */}
              <div className="flex-1 w-full max-w-[680px] xl:max-w-[720px] flex items-center justify-center lg:justify-end">
                <img
                  src="/images/services/automation-audit-ecosystem.png"
                  alt="Connect across your ecosystem - Automation Audit"
                  className="w-full h-auto object-contain block select-none pointer-events-none"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Real Solutions. Real Impact. Testimonials Section matching previous pages */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(248, 250, 252, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col items-center">
            {/* Eyebrow */}
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B] text-center select-none">
              TRUSTED BY BUSINESSES THAT INNOVATE
            </p>

            {/* Main Heading */}
            <h2 className="mt-3.5 text-[32px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-center">
              <span className="text-[#0F172A]">Real Solutions. </span>
              <span className="text-[#1D70F7]">Real Impact.</span>
            </h2>

            {/* Description */}
            <p className="mt-4 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#556987] text-center max-w-[660px]">
              We partner with organizations across industries to identify high-impact automation opportunities that drive efficiency, improve decision-making, and create measurable business value.
            </p>

            {/* Testimonial Card with Prev/Next Controls */}
            <div className="mt-12 sm:mt-14 w-full flex items-center justify-center gap-3 sm:gap-6 lg:gap-8">
              {/* Prev Button */}
              <button
                onClick={handlePrevTestimonial}
                aria-label="Previous testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Center Card */}
              <div
                className="w-full max-w-[672px] p-7 sm:p-[40px] rounded-[24px] flex flex-col gap-[15.5px] border"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  borderColor: "rgba(226, 232, 240, 1)",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Quote Icon */}
                <div className="text-[#1D70F7]">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>

                {/* Quote Text */}
                <p className="text-[15px] sm:text-[16.5px] leading-[26px] sm:leading-[28px] text-[#334155] italic">
                  “{TESTIMONIALS[activeTestimonial].quote}”
                </p>

                {/* Author Info */}
                <div className="mt-3 flex items-center gap-4">
                  {TESTIMONIALS[activeTestimonial].image ? (
                    <div className="w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-full border-[3px] border-[#00D2EE] overflow-hidden shrink-0 shadow-sm">
                      <img
                        src={TESTIMONIALS[activeTestimonial].image}
                        alt={TESTIMONIALS[activeTestimonial].name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-col">
                    <span className="font-bold text-[19px] sm:text-[22px] text-[#0F172A] leading-tight tracking-tight">
                      {TESTIMONIALS[activeTestimonial].name}
                    </span>
                    <span className="text-[14px] sm:text-[15.5px] text-[#5A6E85] mt-1 font-medium">
                      {TESTIMONIALS[activeTestimonial].role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextTestimonial}
                aria-label="Next testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="mt-8 flex items-center justify-center gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    activeTestimonial === idx
                      ? "w-6 h-2 bg-[#1D70F7]"
                      : "w-2 h-2 bg-[#BFDBFE] hover:bg-[#93C5FD]"
                  }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Ready to Unlock Automation Opportunities Globe CTA Section matching user screenshot */}
        <section
          id="cta"
          className="relative w-full overflow-hidden flex items-center py-20 sm:py-24 lg:py-28"
          style={{ backgroundColor: "#000E1B", minHeight: 440 }}
        >
          {/* Background globe image with BOLDER BRIGHTER TOGETHER baked in */}
          <img
            src="/images/services/agentic-ai-globe-banner.png"
            alt="Let's Build a Smarter More Efficient Tomorrow"
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-right lg:object-center pointer-events-none select-none"
          />

          {/* Subtle overlay on smaller screens for crisp text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#000E1B]/90 via-[#000E1B]/50 to-transparent lg:hidden pointer-events-none" />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
              {/* Left Content */}
              <div className="max-w-[780px]">
                {/* Eyebrow */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#8EA3BD]">
                  READY TO UNLOCK AUTOMATION OPPORTUNITIES ??
                </p>

                {/* Heading */}
                <h2 className="mt-4 text-[28px] sm:text-[36px] lg:text-[42px] xl:text-[48px] font-extrabold text-white leading-[1.12] tracking-tight">
                  <span>Let’s Build a Smarter </span>
                  <span className="text-[#1D70F7]">More</span>
                  <span className="block text-[#00D2EE]">Efficient Tomorrow.</span>
                </h2>

                {/* Paragraph */}
                <p className="mt-4 sm:mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#A2B6CE] max-w-[480px]">
                  Assess, Automate, Grow - With Simpel.
                </p>

                {/* CTA Button */}
                <div className="mt-8">
                  <button
                    onClick={handleTalkToTeam}
                    className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-bold text-[#04101D] transition-all duration-300 hover:scale-[1.03] active:scale-95 cursor-pointer"
                    style={{
                      boxShadow:
                        "0 0 24px 3px rgba(47, 115, 246, 0.75), 0 0 45px rgba(0, 210, 238, 0.35)",
                    }}
                  >
                    <span>Talk to Our Team</span>
                    <ArrowRight
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      strokeWidth={2.2}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AutomationAuditPage;
